import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function Login() {
  const { t } = useTranslation();
  const { login, error } = useAuth();
  const [email, setEmail] = useState("admin@wineemotion.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex flex-col p-10 sm:p-14 justify-between noise-bg">
        <div className="flex items-center gap-2">
          <BrandLogo size="xl" />
          <div>
            <div className="font-display font-bold text-zinc-950 leading-none">Wineemotion</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
              {t("login.footer_eyebrow", "Assistenza · Portale interno")}
            </div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0 lg:mt-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
            {t("login.eyebrow")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-zinc-950">
            {t("login.title_line1")}<br />{t("login.title_line2")}
          </h1>
          <p className="text-zinc-500 mt-4 text-sm max-w-sm">
            {t("login.subtitle")}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-500 font-medium">
                {t("login.email_label")}
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm h-11"
                placeholder="tuo.nome@wineemotion.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-500 font-medium">
                {t("login.password_label")}
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-sm h-11"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-brand hover:bg-brand-dark text-white rounded-sm font-medium"
            >
              {loading ? t("common.loading") : t("login.submit")}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>
        </div>

        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-10">
          {t("login.footer")}
        </div>
      </div>

      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/11851422/pexels-photo-11851422.jpeg)",
        }}
      >
        <div className="absolute inset-0 bg-zinc-950/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/70 mb-3">
            {t("login.side_eyebrow")}
          </div>
          <div className="font-display text-3xl font-bold tracking-tight max-w-sm">
            {t("login.side_title")}
          </div>
        </div>
      </div>
    </div>
  );
}
